import React, { useContext} from 'react';
import GetState from '../../GetState';
import { QuestRoute } from '../../api/QuestRoutes';
import { IsAuthorized, userRoles } from '../../RoleProtected';
import { generateId, shortHandDateTime } from '../../utility/functions';
import styled from 'styled-components';
import Table, { Ctd } from '../../table/Table';
import Col from '../../layout/Col';
import Image from '../../Image';
import Button from '../../forms/Button';
import ModalFactory from '../../Modal';
import LinkTo from '../../forms/LinkTo';
import ManagementRow from '../../table/ManagementRow';
import HamQuest from '../../api/HamQuestGiver';
import Row from '../../layout/Row';
import Flag from './Flag';
import CheckMark from '../../CheckMark';
import { UserContexts } from '../../stateProvider/UsersContext';
import AddNewCustomer from '../../processors/AddNewCustomer';
import RequireFeature, { Features } from '../../RequireFeature';
import { renderCustomerLink, hasCustomer } from './LicensePlateReads';

const Wrapper = styled.div`
    width: 100%;`

const CustomerPlateReads = ({ customerId, plate, ...props }) => {
    
    let thisRoute;
    if(customerId){
        thisRoute = QuestRoute.plateReads.customer(customerId);
    } else {
        thisRoute = QuestRoute.plateReads.plate(plate);
    }
    
    const userContext = useContext(UserContexts);
    const { user, washClub} = userContext;

    const [data, refreshData] = GetState(thisRoute);
    const canViewTransactions =  IsAuthorized(userRoles.viewAuditReports);
    const canViewCustomers = IsAuthorized(userRoles.viewCustomers);

    const renderActionButtons = (e) => {
        var r = [];
        
        var blockable = ['UnregisteredPlate'];
        
        if(customerId && e.flags.includes('UnregisteredPlate'))
        {
            //add backup plate
            r.push(<Button key={generateId()} icon='plus' onClick={() => addBackupPlateForCustomer(e.customerId, e.customerName, e.plate, e.transRecordId)} label='Add Backup Plate'/>);

            //add family member
            r.push(<Button key={generateId()} icon='family' onClick={() => CreateCustomerAccount(e.customerId, e.plate, e.transRecordId, true )} label='Add Child Account'/>);
        }

        //block account
        if(customerId && e.flags.some(f => blockable.includes(f)))
            r.push(<Button key={generateId()} icon='lock' onClick={() => showBlockOptionsModal(e.customerId, e.customerName, e.plateReadId, e.transRecordId)} label='Block Club'/>);

        //mark resolved / clear flags
        if(e.flags?.length > 0)
            r.push(<Button key={generateId()} icon='checkCircle' onClick={() => markResolved(e.transRecordId)} label='Mark Resolved' />)
        
        if(!hasCustomer(e))
            r.push(<Button key={generateId()} icon='userPlus' onClick={() => CreateCustomerAccount(e.customerId, e.plate, e.transRecordId, false)} label='Make Customer Account'/>);

        //remove customer from plate read
        if(hasCustomer(e))
            r.push(
                <Button
                    key={generateId()}
                    icon='userMinus'
                    label='Remove Customer'
                    onClick={() => removeCustomerFromPlateRead(e.plateReadId)}
                />
            );

        return r;
    }

    const addBackupPlateForCustomer = (customerId, customerName, plate, transRecordId) => {
        
        const payload = {
                status: 'Active',
                mediaType: 'LicensePlate',
                backupPlate: true,
                mediaId: plate
        }
        
        ModalFactory.confirmAndProcess({
            confirmation: {
                title: 'Add Plate',
                body: <span>Add plate <b>{plate}</b> as backup media for {customerName}?</span>
            },
            promise: {
                title: 'Adding',
                body: `Adding, please wait.`,
                callback: () => HamQuest.post(QuestRoute.user.mediaIDs(customerId), payload)
                    .then(() => {
                        userContext.refreshUser();
                        markResolvedCallback(transRecordId);                        
                    })
            },
            confirmed: {
                title: 'Success',
                body: `${plate} added as a backup License Plate.`,
                type: 'success'
            }
        })
    }

    const CreateCustomerAccount = async (customerId, plate, transRecordId, createAsChild) => {               
        const childProps = createAsChild ? {isChild: true, parentId: customerId} : {};
        const modalId = generateId();

        if(createAsChild && (!user || user.userId !== customerId))
            ModalFactory.show({
                id: modalId,
                title: 'Add Child Account',
                type: 'error',
                body: <>Error loading parent user details.</>
            });
        else if (createAsChild && washClub.status !== 'Active')
            ModalFactory.show({
                id: modalId,
                title: user.fullName + ` - Add Child Account`,
                type: 'error',
                body:   <>
                        <p>The user's wash club account is in <b>{userContext.washClub.status}</b> status.</p> 
                        <p>Cannot add a child account at this time.</p>
                        </>
            });
        else
            ModalFactory.show({
                id: modalId,
                title: createAsChild ? user.fullName + ` - Add Child Account` : 'New Customer',
                type: 'info',
                body: <AddNewCustomer modalId={modalId} license={plate} {...childProps} callback={() => {
                    userContext.refreshWC();
                    markResolvedCallback(transRecordId);
                }} />
            });
    }

    const showBlockOptionsModal = (customerId, customerName, plateReadId) => {
        const id = generateId();

        let body = <></>;
        let buttons = [];
        let modalType = 'info';
        
        switch(washClub?.status)
        {
            case 'Suspended':
                modalType = 'warning';
                body =  <>
                            <p>The user wash club account is already in <b>Suspended</b> status.</p>
                            <div>If desired, you can add this transaction to the list of violations</div>
                            <div>or kick the user from the club and immediately deactivate</div>
                            <div>the user wash club account.</div>
                        </>
                buttons.push(<Button label='Make Them So Much More Suspendeded...ed!' onClick={() => {ModalFactory.close(id); setClubSharedStatusForCustomer(customerId, customerName, plateReadId);}}/>);
                buttons.push(<Button label='Kick From Club' onClick={() => {ModalFactory.close(id); kickFromClub(customerId, customerName, plateReadId);}}/>);
                buttons.push(<Button label='Cancel' onClick={() => {ModalFactory.close(id)}}/>);
                break;
            case 'Inactive':
                modalType = 'error';
                body =  <>
                            <div>The wash club account for {user.fullName} is in <b>Inactive</b> status.</div>
                        </>
                        buttons.push(<Button label='close' onClick={() => {ModalFactory.close(id)}}/>); 
                break;
            default: 
                body =  <>
                            <p>Block club access for {user.fullName}?</p>
                            <p>
                                <div>Suspend access pending user payment for the vended wash</div>
                                <div>or kick the user from the club and immediately deactivate</div>
                                <div>the user wash club account.</div>
                            </p>
                        </>
                buttons.push(<Button label='Suspend Access' onClick={() => {ModalFactory.close(id); setClubSharedStatusForCustomer(customerId, customerName, plateReadId);}}/>);
                buttons.push(<Button label='Kick From Club' onClick={() => {ModalFactory.close(id); kickFromClub(customerId, customerName, plateReadId);}}/>);
                buttons.push(<Button label='Cancel' onClick={() => {ModalFactory.close(id)}}/>);                        
                break;
        }

        ModalFactory.show({
            id,
            type: modalType,           
            title: userContext.user.fullName + ' - Block Club Access',
            body:   body,
            hideClose: true,
            buttons: buttons            
        });
    }

    const setClubSharedStatusForCustomer = (customerId, customerName, plateReadId) => {
        const modalId = generateId();
        ModalFactory.promise({
            id: modalId,
            title: 'Updating',
            body: `Updating, please wait.`,    
        });

        HamQuest.post(QuestRoute.user.washClubSetClubShared(customerId, plateReadId))
        .then(() => {
            ModalFactory.update({
                id: modalId,
                type: 'success',
                title: 'Success',
                body: <>Wash club set to SUSPENDED status for {customerName}.</> 
            })
            .then(() => {
                if(userContext.userId === customerId)
                    userContext.manualRefresh(customerId);
                userContext.refreshWC();
                refreshData(); 
            });
        })
        .catch(ex => {
            ModalFactory.close(modalId);
            ModalFactory.catch(ex);
            userContext.refreshWC();
            refreshData(); 
        });
    };    

    const kickFromClub = (customerId, customerName, plateReadId) => {
        const id = generateId();
        ModalFactory.promise({
            id,
            title: 'Updating',
            body: `Updating, please wait.`,    
        });

        HamQuest.post(QuestRoute.user.washClubKickFromClub(customerId, plateReadId))
        .then(() => {
            ModalFactory.update({
                id,
                type: 'success',
                title: 'Success',
                body: `Club status set to Inactive.`
            })
            .then(() => {
                userContext.refreshWC();
                refreshData(); 
            });
        })
        .catch(ex => {
            ModalFactory.close(id);
            ModalFactory.catch(ex);
            userContext.refreshWC();
            refreshData(); 

        });
    }

    const markResolved = (transRecordId) => {       
        ModalFactory.confirmAndProcess({
            confirmation: {
                title: 'Mark Resolved',
                type: 'warning',
                body: <>
                        <p>This will mark all flags as resovled for transaction # {transRecordId}.</p>
                        <p>Are you sure?</p>
                      </>,
            },
            promise: {
                title: 'Updating',
                body: `Updating, please wait.`,
                callback: () => markResolvedCallback(transRecordId)
            },
            confirmed: {
                type: 'success',
                title: 'Success',
                body: `All flags marked as resolved for transaction.`            
            }
        });        
    };

    const markResolvedCallback = (transRecordId) => {
        const payload = [
                transRecordId
        ];
        return new Promise(async (resolve, reject) => {
                await HamQuest.post(QuestRoute.plateReads.markResolved, payload)
                .then(resolve(true))
                .catch(reject({ error: { errorMessage: 'Error markings alert flags as resolved.' } }))
                .finally(() => {                    
                    userContext.refreshWC();
                    refreshData();
                })
        })
    }

    const removeCustomerFromPlateRead = (plateReadId) => {
        ModalFactory.confirmAndProcess({
            confirmation: {
                title: 'Remove Customer',
                type: 'warning',
                slideToAccept: true,
                body: <>
                    <p>This will remove the current customer from this plate read.</p>
                    <p>Are you sure?</p>
                </>
            },
            promise: {
                title: 'Updating',
                body: 'Removing customer, please wait.',
                callback: () => HamQuest.post(QuestRoute.plateReads.removeCustomer, { plateReadId })
            },
            confirmed: {
                type: 'success',
                title: 'Success',
                body: 'Customer removed from plate read.'
            },
            finally: () => {
                userContext.refreshWC();
                refreshData();
            }
        });
    }

    const tableConfig = {
        data,
        headers: [
            {
                label: 'Date',
                propKey: 'dateRead',
                type: 'date'
            }, {
                label: 'Plate',
                propKey: 'plate',
            }, {
                label: 'Plate Images',
                propKey: 'plate'
            }, {
                label: 'Location',
                propKey: 'location',
                options: true,
            }, {
                label: 'Record',
                propKey: 'transRecordId',
            }, {
                label: 'Transaction Type',
                propKey: 'transactionType'
            },  {
                label: 'Alerts',
                propKey: 'flags'
            }, {
                label: 'Customer',
                propKey: 'customerName',
                show: false
            }, {
                label: 'Message',
                propKey: 'kioskMessage',
                show: false
            } 
        ],
        quickSort: [
            {
                label: 'Alerts',
                propKey: 'flags',
                query: /.+/i,
                color: '#93090e',
            },

        ],
        renderFormat: (e) => (
            <ManagementRow
                key={generateId()}
                controls={renderActionButtons(e)} 
                layout='elipsis'            
            >
                <td>{shortHandDateTime(e.dateRead)}</td>
                <td>
                    {e.plate}
                </td>
                <Ctd>
                    <Col>
                        <Image
                            src={'data:image/gif;base64,' + e.smallPlateImage}
                            modalTitle={e.plate}
                            alt='Car'
                        />
                        <Button
                            label='Full Image'
                            onClick={() => ModalFactory.show({ title: props.plate, body: <Image src={e.fullPlateImageUrl} /> })}
                        />
                    </Col>
                </Ctd>
                <td>{e.location}</td>
                <td>
                    {canViewTransactions ?
                        <LinkTo
                            target='_blank'
                            to={'/reporting/sales/transaction/' + e.transRecordId}>{e.transRecordId}</LinkTo> : <div>{e.transRecordId}</div>
                    }                                                                
                </td>
                <td>
                    {e.transactionType}
                </td>
                <td>
                    <Row>
                        {e.flags.map((flag) => (
                                <Flag key={generateId()} flag={flag} />                            
                            ))
                        }
                    </Row>                    
                </td>
                <td>{ !customerId && e.customerName != 'Visitor' ? renderCustomerLink(e, canViewCustomers) : e.customerName }</td>
                <td>
                    {e.kioskMessage}
                </td>
            </ManagementRow>
        ) 
    };

    return (
        <RequireFeature requiredFeatures={Features.PlateReads}>
            <h4>Plate Read History</h4>
            <Table config={tableConfig} />
        </RequireFeature>
    ); 
}

export default CustomerPlateReads;
